import * as Sentry from "@sentry/react";
import { Check, Info, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { APIError } from "@/api/utils/errors";
import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import { getDraftDocx } from "../../../api/drafts-v2";
import { FilesystemAPI } from "../../caseFiles/api/filesystemApi";
import { nodeTitle } from "../../caseFiles/nodeNames";
import { downloadDocx } from "../../draftEditor/docxExport";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { previewLabel, suggestFolder, type CopySource } from "./caseFileCopies";
import { visibleRows } from "./treeRows";

type Props = {
  /** The work product to copy; the dialog is open while one is set. */
  source: CopySource | null;
  onOpenChange: (open: boolean) => void;
};

const ROOT_NAME = "Case Files";
const INDENT = 14;

const FieldLabel: FC<{ children: string; htmlFor?: string }> = ({
  children,
  htmlFor
}) => (
  <label htmlFor={htmlFor} className="text-sm font-medium text-fg-2">
    {children}
  </label>
);

/** The backend could not turn the draft into a PDF (it has already reported it). */
const isPdfConversionFailure = (error: unknown): boolean =>
  error instanceof APIError && error.details?.code === "pdf_conversion_failed";

const downloadDraftDocx = async (draftId: string) => {
  try {
    const { bytes, filename } = await getDraftDocx(draftId);
    downloadDocx(bytes, filename);
  } catch (error) {
    Sentry.captureException(error);
    toast.error("Couldn't download the DOCX.");
  }
};

/**
 * Add to Case Files: copy a draft or form into the case as a snapshot, so it
 * can be an exhibit. Picks the folder (with a suggestion), the name, PDF or
 * DOCX, and Include in Packet with the label it will take.
 */
export const AddToCaseFilesDialog: FC<Props> = ({ source, onOpenChange }) => {
  const { individualId, open, invalidate } = useCaseIde();
  const { fs } = useCaseData();
  const isForm = source?.kind === "form";

  const folderRows = useMemo(() => {
    const folders = fs.nodes.filter(
      (n) => n.kind === "folder" && !n.is_deleted
    );
    return visibleRows(folders, new Set(folders.map((f) => f.id)));
  }, [fs.nodes]);
  const suggested = useMemo(
    () =>
      source
        ? suggestFolder(
            folderRows.map((r) => r.node),
            source
          )
        : null,
    [folderRows, source]
  );

  const [dest, setDest] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [format, setFormat] = useState<"pdf" | "docx">("pdf");
  const [include, setInclude] = useState(true);
  const [saving, setSaving] = useState(false);
  const [conversionFailed, setConversionFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!source) return;
    setDest(suggested);
    setName(source.title);
    setFormat("pdf");
    setInclude(true);
    setConversionFailed(false);
    // Reset only when a new source opens the dialog.
  }, [source?.kind, source?.id]);

  const preview = useMemo(
    () => previewLabel(fs.nodes, fs.scheme, dest, include),
    [fs.nodes, fs.scheme, dest, include]
  );
  const labelOf = (id: string) => fs.labels[id] ?? null;
  const destName = dest
    ? nodeTitle(fs.nodes.find((n) => n.id === dest) ?? { name: ROOT_NAME })
    : ROOT_NAME;

  const add = async () => {
    if (!source || !name.trim()) return;
    setSaving(true);
    setConversionFailed(false);
    try {
      const result = await FilesystemAPI.promote(individualId, {
        source: { kind: source.kind, id: source.id },
        parent_id: dest,
        title: name.trim(),
        format: isForm ? "pdf" : format,
        include
      });
      const title = nodeTitle(result.node);
      const label = result.exhibit_label;
      onOpenChange(false);
      await fs.refresh();
      invalidate(["fs", "packet"]);
      open({ kind: "file", id: result.node.id, title });
      toast.success(
        `Added ${title} to Case Files${label ? ` as Exhibit ${label}` : ""}`,
        {
          description: include
            ? "The packet will rebuild with it."
            : `Filed under ${destName}, outside the packet.`,
          action: {
            label: "Undo",
            onClick: () => {
              void FilesystemAPI.deleteNodes(individualId, [result.node.id])
                .then(() => fs.refresh())
                .then(() => invalidate(["fs", "packet"]))
                .catch(() => toast.error(`Couldn't remove ${title}.`));
            }
          }
        }
      );
      if (result.unresolved_citations) {
        const n = result.unresolved_citations;
        toast.warning(
          `${n} citation${n === 1 ? "" : "s"} in the copy point${
            n === 1 ? "s" : ""
          } at exhibits no longer in the packet.`,
          { description: "They print as “[exhibit removed]”." }
        );
      }
    } catch (error) {
      if (source.kind === "draft" && isPdfConversionFailure(error)) {
        setConversionFailed(true);
        return;
      }
      Sentry.captureException(error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Couldn't add it to Case Files."
      );
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (!source) return;
    setDownloading(true);
    await downloadDraftDocx(source.id);
    setDownloading(false);
  };

  return (
    <Dialog open={!!source} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 gap-0 p-0 sm:max-w-120"
        data-testid="add-to-case-files"
      >
        <DialogHeader className="gap-1 px-5 pt-4">
          <DialogTitle className="text-md">Add to Case Files</DialogTitle>
          <DialogDescription className="text-sm">
            Copies a work product into the case so it can be an exhibit in the
            packet.
          </DialogDescription>
        </DialogHeader>
        {source ? (
          <div className="flex flex-col gap-3.5 px-5 pt-3.5 pb-4">
            <div className="flex items-center gap-2.5 rounded-lg bg-fill-1 px-3 py-2">
              <FileTypeIcon
                name={isForm ? "form.pdf" : "draft.docx"}
                size={20}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-base font-medium text-fg">
                  {source.title}
                </span>
                {source.detail ? (
                  <span className="truncate text-sm text-fg-3">
                    {source.detail}
                  </span>
                ) : null}
              </div>
              <Tag>Work Products</Tag>
            </div>

            <div className="flex flex-col gap-1.5">
              <FieldLabel>Destination</FieldLabel>
              <div
                role="listbox"
                aria-label="Destination"
                className="max-h-48 overflow-y-auto rounded-md p-1 ring-1 ring-line-2"
              >
                {[
                  { id: null, title: ROOT_NAME, depth: -1 },
                  ...folderRows.map((r) => ({
                    id: r.node.id,
                    title: nodeTitle(r.node),
                    depth: r.depth
                  }))
                ].map((row) => {
                  const selected = row.id === dest;
                  const label = row.id ? labelOf(row.id) : null;
                  return (
                    <button
                      key={row.id ?? "root"}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => setDest(row.id)}
                      className={cn(
                        "flex h-6 w-full items-center gap-1.5 rounded-xs pr-2 text-left text-base",
                        selected
                          ? "bg-fill-2 text-fg"
                          : "text-fg-2 hover:bg-fill-1"
                      )}
                      style={{ paddingLeft: 8 + (row.depth + 1) * INDENT }}
                    >
                      <FileTypeIcon isFolder size={14} />
                      <span className="min-w-0 flex-1 truncate">
                        {row.title}
                      </span>
                      {row.id && row.id === suggested ? (
                        <Tag>Suggested</Tag>
                      ) : null}
                      {label ? (
                        <span className="font-mono text-sm text-fg-4">
                          {label}
                        </span>
                      ) : null}
                      <span className="flex w-3.5 justify-center">
                        {selected ? (
                          <Check className="size-3.5 text-primary" />
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
              {suggested ? (
                <span className="text-sm text-fg-3">
                  Suggested from what the {isForm ? "form" : "draft"} is; pick
                  any folder.
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="add-to-case-files-name">Name</FieldLabel>
              <Input
                id="add-to-case-files-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <FieldLabel>Format</FieldLabel>
              <Tabs
                value={isForm ? "pdf" : format}
                onValueChange={(value) =>
                  setFormat(value === "docx" ? "docx" : "pdf")
                }
              >
                <TabsList variant="segmented" className="w-fit">
                  <TabsTrigger value="pdf" className="px-2.5">
                    PDF (Recommended for Exhibits)
                  </TabsTrigger>
                  <TabsTrigger
                    value="docx"
                    disabled={isForm}
                    className="px-2.5"
                  >
                    DOCX
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <span className="text-sm text-fg-3">
                {isForm
                  ? "Forms are added as PDF."
                  : format === "pdf"
                  ? "Renders the draft as it will print. Page stamps and the tab apply as for any exhibit."
                  : "Keeps an editable copy; it is converted to PDF each time the packet builds."}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-base text-fg">Include in Packet</span>
                <span className="flex flex-wrap items-center gap-1.5 text-sm text-fg-3">
                  {include && preview.label ? (
                    <>
                      Will be <Tag mono>{`Exhibit ${preview.label}`}</Tag>
                      {preview.after ? `after ${preview.after}` : null}
                    </>
                  ) : include ? (
                    "Included in the packet without an exhibit number."
                  ) : (
                    "Kept in Case Files only, without an exhibit label."
                  )}
                </span>
              </div>
              <Switch
                checked={include}
                onCheckedChange={setInclude}
                aria-label="Include in Packet"
              />
            </div>

            <div className="flex items-start gap-2">
              <Info className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
              <span className="text-sm text-fg-3">
                A snapshot copy. Later edits to the {isForm ? "form" : "draft"}{" "}
                don't change it; the file is flagged when the{" "}
                {isForm ? "form" : "draft"} changes so you can update it.
              </span>
            </div>

            {conversionFailed ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2.5"
              >
                <TriangleAlert
                  className="mt-0.5 size-3.5 shrink-0 text-danger"
                  aria-hidden
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-base text-danger">
                    Couldn't convert {source.title} to PDF.
                  </span>
                  <span className="text-sm text-fg-2">
                    Download the DOCX, save it as a PDF in Word, and upload that
                    to Case Files.
                  </span>
                </div>
                <Button
                  size="xs"
                  disabled={downloading}
                  onClick={() => void download()}
                >
                  {downloading ? <Spinner className="size-3.5" /> : null}
                  Download DOCX
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
        <DialogFooter className="mx-0 mb-0 px-5 py-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!source || !name.trim() || saving}
            onClick={() => void add()}
          >
            {saving ? <Spinner className="size-3.5" /> : null}
            Add to Case Files
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
